import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import type { SupplierProductItemInput } from "../../../.generated/api-client/models/SupplierProductItemInput";
import type { TargetEmployeeDisplay } from "../../../.generated/api-client/models/TargetEmployeeDisplay";
import type { WorkRequestStepOut } from "../../../.generated/api-client/models/WorkRequestStepOut";
import type { VisualLedgerTone } from "../../components/visual-ledger/VisualLedger";
import {
  categoryLabelForItem,
  type CategoryPathIndex,
} from "../../features/inventory-category/categoryPath";
import {
  businessDateKey,
  formatBusinessDateTime,
  formatBusinessMonthDayTime,
} from "../../utils/businessTime";
import {
  asArray,
  asRecord,
  numberValue,
  stringValue,
} from "../../services/api";
import { REASON_CN, REQUEST_TYPE_CN, STATUS_CN } from "./constants";
import type {
  ApprovalDetail,
  ApprovalItem,
  DetailRow,
  LeaveQuotaDetail,
  LeaveUnitDetail,
  ProductItemValues,
  ScheduleChangeDetail,
  SupplierProductSnapshot,
} from "./types";

export function statusLabel(status: unknown): string {
  const value = stringValue(status, "");
  return STATUS_CN[value] ?? (value || "待处理");
}

export function statusColor(status: string): VisualLedgerTone {
  if (status === "approved") return "success";
  if (status === "rejected" || status === "cancelled") return "danger";
  if (status === "returned") return "warning";
  if (status === "withdrawn" || status === "expired") return "muted";
  if (status === "in_review") return "info";
  return "warning"; // pending → 待处理
}

export function sortApprovalStepsForDisplay(
  steps: WorkRequestStepOut[],
): WorkRequestStepOut[] {
  return steps
    .slice()
    .sort(
      (a, b) =>
        (a.round_no ?? 1) - (b.round_no ?? 1) || a.step_order - b.step_order,
    );
}

export function formatDateTime(value: unknown): string {
  return formatBusinessDateTime(stringValue(value, ""), { seconds: false });
}

export function employmentTenureText(
  hireDate: string,
  endDate: string,
): string {
  const started = dayjs(hireDate);
  const ended = dayjs(endDate);
  if (
    !started.isValid() ||
    !ended.isValid() ||
    ended.isBefore(started, "day")
  ) {
    return "";
  }
  const months = ended.diff(started, "month");
  if (months < 1) {
    return `在职 ${ended.startOf("day").diff(started.startOf("day"), "day")} 天`;
  }
  if (months < 12) return `在职 ${months} 个月`;
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;
  return `在职 ${years} 年${remainingMonths ? ` ${remainingMonths} 个月` : ""}`;
}

/**
 * 列表里的提交时间 — 近两天说人话 (今天 / 昨天), 更早给月日。
 * `now` 只供单测固定时间。
 */
export function formatSubmittedAtRelative(
  value: unknown,
  now: Dayjs = dayjs(),
): string {
  const raw = stringValue(value, "");
  if (!raw) return "—";
  const dateKey = businessDateKey(raw);
  const nowKey = businessDateKey(now.toDate());
  if (!dateKey || !nowKey) return "—";
  const days = dayjs(nowKey).diff(dayjs(dateKey), "day");
  const formatted = formatBusinessMonthDayTime(raw);
  const time = formatted.slice(-5);
  if (days === 0) return `今天 ${time}`;
  if (days === 1) return `昨天 ${time}`;
  return formatted;
}

/**
 * 这条申请等了多久 (提交时间 → 现在)。审批人扫列表时第一眼要看的就是"谁被晾着"。
 * 后端 expires_in_seconds 是"过期"口径, 已被 Ricky 退役 (审批流没有过期), 故按提交时间自己算。
 */
export function formatWaitedFor(value: unknown, now: Dayjs = dayjs()): string {
  const raw = stringValue(value, "");
  if (!raw) return "";
  const parsed = dayjs(raw);
  if (!parsed.isValid()) return "";
  const minutes = now.diff(parsed, "minute");
  if (minutes < 1) return "刚刚提交";
  if (minutes < 60) return `已等 ${minutes} 分钟`;
  const hours = now.diff(parsed, "hour");
  if (hours < 24) return `已等 ${hours} 小时`;
  return `已等 ${now.diff(parsed, "day")} 天`;
}

export function stringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (item): item is string => typeof item === "string" && item.length > 0,
  );
}

export function getResponseRows(response: unknown): Record<string, unknown>[] {
  const root = asRecord(response);
  const data = asRecord(root.data ?? response);
  return asArray(
    data.items ?? data.employees ?? data.requests ?? data.work_requests,
  );
}

export function mapProductItems(
  items: ProductItemValues[],
): SupplierProductItemInput[] {
  return items.map((item) => ({
    item_name: item.itemName.trim(),
    category_id: item.categoryId,
    spec: item.spec?.trim() || null,
    default_unit: item.defaultUnit.trim(),
    loose_unit: item.looseUnit.trim(),
    allow_loose: true,
    conversion_factor: item.conversionFactor,
  }));
}

export function buildInclusiveDates(range: [Dayjs, Dayjs]): string[] {
  const [start, end] = range;
  const dates: string[] = [];
  let cursor = start.startOf("day");
  const last = end.startOf("day");
  while (cursor.isSame(last) || cursor.isBefore(last)) {
    dates.push(cursor.format("YYYY-MM-DD"));
    cursor = cursor.add(1, "day");
  }
  return dates;
}

export function typeLabel(
  requestType: string,
  payload: Record<string, unknown>,
): string {
  if (requestType !== "leave")
    return REQUEST_TYPE_CN[requestType] ?? "流程申请";
  return stringValue(payload.leave_scope, "") === "store_manager_monthly_rest"
    ? "店长月休"
    : "员工请假";
}

export function punchTypeLabel(types: string[]): string {
  if (types.length === 1 && types[0] === "in") return "仅补上班";
  if (types.length === 1 && types[0] === "out") return "仅补下班";
  return "上下班双补";
}

export function approvalChainRows(
  payload: Record<string, unknown>,
): DetailRow[] {
  return asArray(payload.approval_chain).map((node) => {
    const nodeLabel = stringValue(node.label, "审批人");
    const fullName = stringValue(node.full_name, "");
    const status = stringValue(node.status, "");
    const statusText =
      status === "done"
        ? "已完成"
        : status === "current"
          ? "当前处理节点"
          : "下一节点";
    return {
      label: fullName ? `${nodeLabel} ${fullName}` : nodeLabel,
      value: statusText,
    };
  });
}

export function parseSupplierProducts(
  itemsValue: unknown,
): SupplierProductSnapshot[] {
  return asArray(itemsValue).map((item) => ({
    itemName: stringValue(item.item_name, "未命名商品"),
    categoryId: numberValue(item.category_id),
    categoryName: stringValue(item.category_name, ""),
    spec: stringValue(item.spec, ""),
    defaultUnit: stringValue(item.default_unit, ""),
    looseUnit: stringValue(item.loose_unit, ""),
    conversionFactor: numberValue(item.conversion_factor),
  }));
}

export function supplierProductSummary(
  items: SupplierProductSnapshot[],
  categoryPathIndex: CategoryPathIndex = new Map(),
): string {
  if (items.length === 0) return "未填写商品";
  return items
    .slice(0, 3)
    .map((item) => {
      const name = item.itemName || "未命名商品";
      const category = categoryLabelForItem(
        item.categoryId,
        item.categoryName,
        categoryPathIndex,
      );
      const spec = item.spec || "规格未填写";
      const unit = item.defaultUnit || "单位待核对";
      return `${name} · ${category} · ${spec} · ${unit}`;
    })
    .join("、")
    .concat(items.length > 3 ? ` 等 ${items.length} 项` : "");
}

export function productSummary(
  itemsValue: unknown,
  categoryPathIndex: CategoryPathIndex = new Map(),
): string {
  return supplierProductSummary(
    parseSupplierProducts(itemsValue),
    categoryPathIndex,
  );
}

export function leaveSnapshot(payload: Record<string, unknown>): {
  units: LeaveUnitDetail[];
  legacy: boolean;
  quota: LeaveQuotaDetail | null;
} {
  const dates = stringArray(payload.leave_dates);
  const rawUnits = asArray(payload.leave_date_units);
  const legacy = rawUnits.length === 0;
  const sourceUnits = legacy
    ? dates.map((workDate) => ({ work_date: workDate, unit: "full_day" }))
    : rawUnits;
  const units = sourceUnits.map((rawItem): LeaveUnitDetail => {
    const item = asRecord(rawItem);
    const isHalfDay = stringValue(item.unit, "full_day") === "half_day";
    const requiredMinutes = numberValue(item.required_work_minutes, 300);
    return {
      workDate: stringValue(item.work_date, "—"),
      unitLabel: isHalfDay ? "半天" : "整天",
      quotaText: isHalfDay ? "0.5 天" : "1 天",
      attendanceRule: isHalfDay
        ? `有效上下班卡齐全 · 至少 ${requiredMinutes / 60} 小时`
        : "当日按整天休假处理",
    };
  });
  if (payload.quota_total === undefined) return { units, legacy, quota: null };
  const total = numberValue(payload.quota_total);
  const usedBefore = numberValue(payload.quota_used_before);
  const pendingBefore = numberValue(payload.quota_pending_before);
  const current = numberValue(
    payload.quota_day_count,
    numberValue(payload.day_count, dates.length),
  );
  return {
    units,
    legacy,
    quota: {
      total,
      usedBefore,
      pendingBefore,
      current,
      remainingAfter: total - usedBefore - pendingBefore - current,
    },
  };
}

function scheduleClock(value: unknown): string {
  return stringValue(value, "").slice(0, 5);
}

function scheduleCellSnapshotText(value: unknown): string {
  const snapshot = asRecord(value);
  const scheduleType = stringValue(snapshot.schedule_type, "none");
  if (scheduleType === "rest") return "休息";
  if (scheduleType === "none") return "待排";
  return asArray(snapshot.shifts)
    .map((rawShift) => {
      const shift = asRecord(rawShift);
      return `${stringValue(shift.shift_name_snapshot, "班次")} ${scheduleClock(shift.start_snapshot)}-${scheduleClock(shift.end_snapshot)}`;
    })
    .join(" / ");
}

export function scheduleChangeSnapshot(payload: Record<string, unknown>): {
  targetName: string;
  changes: ScheduleChangeDetail[];
  reason: string;
  supersededText: string;
} {
  const changes = asArray(payload.changes).map((rawChange) => {
    const change = asRecord(rawChange);
    const scheduleType = stringValue(change.schedule_type, "rest");
    return {
      workDate: stringValue(change.work_date, "—"),
      beforeText: scheduleCellSnapshotText(change.before),
      afterText:
        scheduleType === "rest"
          ? "休息"
          : `${stringValue(change.shift_name_snapshot, "班次")} ${scheduleClock(change.start_snapshot)}-${scheduleClock(change.end_snapshot)}`,
    };
  });
  return {
    targetName: stringValue(payload.target_name_snapshot, "员工"),
    changes,
    reason: stringValue(payload.reason, "—"),
    supersededText:
      stringValue(asRecord(payload.superseded_by_schedule_save).reason) ===
      "superseded_by_schedule_save"
        ? "店长已直接调整该日排班"
        : "",
  };
}

/**
 * 1c: 入职 / 离职审批的目标员工职级中文拼接 (后端 target_employee_display)。
 * position_cn + level_label (如 "服务员 · L2"); 缺失降级 "—", 不裸显编号。
 */
function targetPositionText(
  display: TargetEmployeeDisplay | null | undefined,
): string {
  const parts = [display?.position_cn, display?.level_label].filter(
    (part): part is string => typeof part === "string" && part.trim() !== "",
  );
  return parts.length > 0 ? parts.join(" · ") : "—";
}

function targetNameText(
  display: TargetEmployeeDisplay | null | undefined,
  fallback: string,
): string {
  const name = display?.full_name;
  return name && name.trim() !== "" ? name.trim() : fallback;
}

/**
 * 1c: 把 detail / list 记录里的 target_employee_display 解析成强类型 (派生自
 * generated TargetEmployeeDisplay, 不手写字段)。缺失或非对象时返回 null,
 * 入职 / 离职分支据此降级容错, 其余类型本就不读此字段。
 */
function parseTargetDisplay(value: unknown): TargetEmployeeDisplay | null {
  if (value === null || value === undefined) return null;
  const record = asRecord(value);
  const display: TargetEmployeeDisplay = {
    full_name: stringValue(record.full_name, "") || null,
    position_cn: stringValue(record.position_cn, "") || null,
    level_label: stringValue(record.level_label, "") || null,
    hire_date: stringValue(record.hire_date, "") || null,
    offboard_date: stringValue(record.offboard_date, "") || null,
  };
  return display;
}

export function payloadRows(
  requestType: string,
  payload: Record<string, unknown>,
  targetDisplay?: TargetEmployeeDisplay | null,
  onboardingSnapshot?: Record<string, unknown> | null,
): {
  rows: DetailRow[];
  risk: string | null;
  summary: string;
  chain: DetailRow[];
} {
  if (requestType === "schedule_change") {
    const snapshot = scheduleChangeSnapshot(payload);
    return {
      rows: [
        { label: "调整员工", value: snapshot.targetName },
        {
          label: "调整日期",
          value: snapshot.changes.map((item) => item.workDate).join("、"),
        },
        { label: "原因", value: snapshot.reason },
      ],
      risk: null,
      summary: `排班调整 · ${snapshot.targetName} · ${snapshot.changes.length} 项`,
      chain: [],
    };
  }

  if (requestType === "leave") {
    const dates = stringArray(payload.leave_dates);
    const dayCount = numberValue(payload.day_count, dates.length);
    const isOverQuota = payload.is_over_quota === true;
    const rows: DetailRow[] = [
      { label: "类型", value: typeLabel(requestType, payload) },
      { label: "日期", value: dates.length ? dates.join("、") : "—" },
      { label: "天数", value: `${dayCount} 天` },
      { label: "原因", value: stringValue(payload.reason) },
    ];
    return {
      rows,
      risk: isOverQuota ? "超额" : null,
      summary: `${typeLabel(requestType, payload)} · ${dates.length ? dates.join("、") : "未选日期"}`,
      chain: approvalChainRows(payload),
    };
  }

  if (requestType === "field_work" || requestType === "field_punch") {
    const dates = stringArray(payload.field_work_dates);
    const fieldWorkType = stringValue(payload.field_work_type, "other");
    const typeText: Record<string, string> = {
      cross_store_support: "跨店支援",
      training: "培训学习",
      business_trip: "出差",
      other: "其他外勤",
    };
    const rows: DetailRow[] = [
      { label: "事由类型", value: typeText[fieldWorkType] ?? "其他外勤" },
      { label: "外勤日期", value: dates.length ? dates.join("、") : "—" },
      { label: "外勤天数", value: `${dates.length} 天` },
      { label: "说明", value: stringValue(payload.reason, "—") },
    ];
    const supportStoreName = stringValue(payload.support_store_name, "");
    if (
      requestType === "field_punch" &&
      fieldWorkType === "cross_store_support"
    ) {
      rows.splice(1, 0, {
        label: "支援门店",
        value: supportStoreName || "提交时未留存门店名称",
      });
    }
    const requestLabel =
      requestType === "field_punch" ? "外勤申请" : "历史带薪外勤";
    return {
      rows,
      risk: null,
      summary: `${requestLabel} · ${dates.length} 天 · ${typeText[fieldWorkType] ?? "其他外勤"}`,
      chain: [],
    };
  }

  if (requestType === "supplier_onboarding") {
    const supplier = asRecord(payload.supplier);
    const rows: DetailRow[] = [
      { label: "类型", value: "供应商准入" },
      {
        label: "供应商",
        value: stringValue(
          payload.supplier_name ?? supplier.name,
          "未命名供应商",
        ),
      },
      { label: "联系人", value: stringValue(supplier.contact_name, "—") },
      { label: "联系电话", value: stringValue(supplier.contact_phone, "—") },
      { label: "商品清单", value: productSummary(payload.items) },
      { label: "申请说明", value: stringValue(payload.note, "—") },
    ];
    return {
      rows,
      risk: null,
      summary: `供应商准入 · ${stringValue(payload.supplier_name ?? supplier.name, "未命名供应商")}`,
      chain: [],
    };
  }

  if (requestType === "supplier_product_add") {
    const supplier = asRecord(payload.supplier);
    const rows: DetailRow[] = [
      { label: "类型", value: "供应商加品" },
      {
        label: "供应商",
        value: stringValue(
          payload.supplier_name,
          `#${numberValue(payload.supplier_id)}`,
        ),
      },
      { label: "联系人", value: stringValue(supplier.contact_name, "—") },
      { label: "联系电话", value: stringValue(supplier.contact_phone, "—") },
      { label: "商品清单", value: productSummary(payload.items) },
      { label: "申请说明", value: stringValue(payload.note, "—") },
    ];
    return {
      rows,
      risk: null,
      summary: `供应商加品 · ${stringValue(payload.supplier_name, `#${numberValue(payload.supplier_id)}`)}`,
      chain: [],
    };
  }

  if (requestType === "reimbursement") {
    const categoryText: Record<string, string> = {
      meal_misc: "餐杂",
      transport: "交通",
      purchase: "采购",
      other: "其他",
    };
    const receiptCount = asArray(payload.receipt_photos).length;
    const amount = stringValue(payload.amount, "0.00");
    const rows: DetailRow[] = [
      { label: "金额", value: `¥${amount}` },
      {
        label: "类别",
        value: categoryText[stringValue(payload.category, "other")] ?? "其他",
      },
      {
        label: "费用日期",
        value: stringValue(payload.expense_date, "—"),
      },
      { label: "事由", value: stringValue(payload.reason, "—") },
      { label: "票据照片", value: `${receiptCount} 张` },
    ];
    return {
      rows,
      risk: null,
      summary: `报销 · ¥${amount} · ${stringValue(payload.expense_date, "日期待核对")}`,
      chain: [],
    };
  }

  if (requestType === "employee_onboarding") {
    // 审批决策面板只留与当下决策直接相关的身份与岗位字段；入职日期不重复展示。
    // 历史读路径仍优先用后端 target_employee_display (姓名 + 职级中文),
    // 历史脏数据缺失时降级到旧 payload.candidate, 都没有才退回占位文案 (不裸显编号)。
    const candidate = asRecord(payload.candidate);
    const position = asRecord(payload.position_request);
    const candidateName = targetNameText(
      targetDisplay,
      stringValue(candidate.full_name, "候选人信息待补全"),
    );
    const positionFallback =
      [stringValue(position.position, ""), stringValue(position.level, "")]
        .filter(Boolean)
        .join(" · ") || "—";
    const snapshotPosition = onboardingSnapshot
      ? [
          stringValue(onboardingSnapshot.position_display_cn, ""),
          onboardingSnapshot.level !== null &&
          onboardingSnapshot.level !== undefined
            ? `L${numberValue(onboardingSnapshot.level)}`
            : "",
        ]
          .filter(Boolean)
          .join(" · ")
      : "";
    const positionText =
      snapshotPosition ||
      (targetDisplay && (targetDisplay.position_cn || targetDisplay.level_label)
        ? targetPositionText(targetDisplay)
        : positionFallback);
    const rows: DetailRow[] = [
      { label: "类型", value: "入职审批" },
      { label: "候选人", value: candidateName },
      { label: "岗位职级", value: positionText },
      {
        label: "直属上级",
        value: onboardingSnapshot
          ? stringValue(onboardingSnapshot.manager_name, "未留存")
          : "未留存",
      },
      {
        label: "备注",
        value: stringValue(payload.note ?? candidate.notes, "—"),
      },
    ];
    const salary = onboardingSnapshot
      ? asRecord(onboardingSnapshot.salary)
      : {};
    if (onboardingSnapshot) {
      rows.splice(3, 0, {
        label: "用工类型",
        value:
          onboardingSnapshot.employment_type === "part_time" ? "兼职" : "全职",
      });
      if (
        onboardingSnapshot.employment_type !== "part_time" &&
        onboardingSnapshot.probation_months !== null &&
        onboardingSnapshot.probation_months !== undefined
      ) {
        rows.splice(4, 0, {
          label: "试用月数",
          value: `${numberValue(onboardingSnapshot.probation_months)} 个月`,
        });
      }
    }
    if (Object.keys(salary).length > 0) {
      const source = stringValue(salary.source, "");
      const partTime = onboardingSnapshot?.employment_type === "part_time";
      rows.splice(
        partTime ? 4 : 5,
        0,
        {
          label: partTime ? "日薪" : "基本月薪",
          value: partTime
            ? salary.daily_rate === null || salary.daily_rate === undefined
              ? "未留存提交时日薪"
              : `¥${stringValue(salary.daily_rate)}/天`
            : salary.base_amount === null || salary.base_amount === undefined
              ? "历史邀请未留存提交时金额"
              : `¥${stringValue(salary.base_amount)}/月`,
        },
        {
          label: "薪资来源",
          value:
            source === "company_standard"
              ? "公司标准"
              : source === "manual"
                ? "手工拟定"
                : "历史标准引用（未留存提交快照）",
        },
      );
    }
    return {
      rows,
      risk: null,
      summary: `入职审批 · ${candidateName}`,
      chain: [],
    };
  }

  if (requestType === "offboarding") {
    // 1c: 优先用后端 target_employee_display (姓名 + 职级中文 + 离职日期), 不裸显编号。
    const employeeName = targetNameText(targetDisplay, "员工信息待补全");
    const offboardDate =
      targetDisplay?.offboard_date ?? stringValue(payload.offboard_date, "—");
    const hireDate = targetDisplay?.hire_date ?? "";
    const tenure = employmentTenureText(hireDate, offboardDate);
    const rows: DetailRow[] = [
      { label: "类型", value: "离职审批" },
      { label: "员工", value: employeeName },
      { label: "职级", value: targetPositionText(targetDisplay) },
      ...(hireDate
        ? [
            { label: "入职日期", value: hireDate },
            { label: "在职时长", value: tenure || "待核对" },
          ]
        : []),
      { label: "离职日期", value: offboardDate || "—" },
      { label: "原因", value: stringValue(payload.reason, "—") },
    ];
    return {
      rows,
      risk: null,
      summary: `离职审批 · ${employeeName}`,
      chain: [],
    };
  }

  const punchTypes = stringArray(payload.punch_types);
  const reasonEnum = stringValue(payload.reason_enum, "");
  const rows: DetailRow[] = [
    { label: "补卡日期", value: stringValue(payload.punch_date) },
    { label: "补卡类型", value: punchTypeLabel(punchTypes) },
    { label: "上班时间", value: stringValue(payload.in_time) },
    { label: "下班时间", value: stringValue(payload.out_time) },
    {
      label: "原因",
      value: `${REASON_CN[reasonEnum] ?? (reasonEnum || "—")}${
        payload.reason_text ? ` · ${stringValue(payload.reason_text)}` : ""
      }`,
    },
  ];
  return {
    rows,
    risk: null,
    summary: `${stringValue(payload.punch_date)} · ${punchTypeLabel(punchTypes)}`,
    chain: [],
  };
}

export function mapApprovalItem(
  record: Record<string, unknown>,
  fallback?: { requesterName?: string; requesterMeta?: string },
): ApprovalItem {
  const payload = asRecord(record.payload);
  const requester = asRecord(record.requester);
  const requestType = stringValue(record.request_type, "");
  const targetDisplay = parseTargetDisplay(record.target_employee_display);
  const parsedPayload = payloadRows(requestType, payload, targetDisplay);
  const supplierProducts = [
    "supplier_onboarding",
    "supplier_product_add",
  ].includes(requestType)
    ? parseSupplierProducts(payload.items)
    : [];
  const summary = stringValue(record.payload_summary, parsedPayload.summary);
  const status = stringValue(record.status, "pending");
  const submittedAtRaw = record.submitted_at ?? record.created_at;

  return {
    requestId: numberValue(record.request_id),
    requestType,
    requester: stringValue(
      requester.full_name ??
        record.requester_name ??
        record.requester_full_name,
      fallback?.requesterName ?? "申请人",
    ),
    requesterMeta: stringValue(
      requester.position_cn,
      fallback?.requesterMeta ?? "",
    ),
    storeName: stringValue(record.store_name, ""),
    typeLabel: typeLabel(requestType, payload),
    status,
    statusLabel: statusLabel(status),
    summary,
    submittedAt: formatDateTime(submittedAtRaw),
    submittedAtLabel: formatSubmittedAtRelative(submittedAtRaw),
    waitedLabel: ["pending", "in_review", "returned"].includes(status)
      ? formatWaitedFor(submittedAtRaw)
      : "",
    risk: parsedPayload.risk,
    supplierProducts,
  };
}

export function mapApprovalDetail(
  response: unknown,
  steps: WorkRequestStepOut[] = [],
  stepsError: string | null = null,
): ApprovalDetail {
  const data = asRecord(asRecord(response).data ?? response);
  const statusSemantics = asRecord(data.status_semantics);
  const currentOwner = asRecord(statusSemantics.current_owner);
  const item = mapApprovalItem(data);
  const payload = asRecord(data.payload);
  const requester = asRecord(data.requester);
  const requestType = stringValue(data.request_type, "");
  // 1c: 入职 / 离职审批详情读后端只读字段 target_employee_display (姓名 + 职级 + 日期)。
  const targetDisplay = parseTargetDisplay(data.target_employee_display);
  const onboardingSnapshot =
    data.onboarding_snapshot === null || data.onboarding_snapshot === undefined
      ? null
      : asRecord(data.onboarding_snapshot);
  const parsedPayload = payloadRows(
    requestType,
    payload,
    targetDisplay,
    onboardingSnapshot,
  );
  const parsedLeave =
    requestType === "leave"
      ? leaveSnapshot(payload)
      : { units: [], legacy: false, quota: null };
  const parsedScheduleChange =
    requestType === "schedule_change"
      ? scheduleChangeSnapshot(payload)
      : { targetName: "", changes: [], reason: "", supersededText: "" };
  const riskSignals = asRecord(data.risk_signals);
  const punches = asArray(data.existing_valid_punches_on_date).map((punch) => ({
    label: stringValue(punch.punch_type, "打卡"),
    value: `${formatDateTime(punch.punched_at ?? punch.punch_at)} · ${stringValue(punch.source, "原始记录")}`,
  }));
  const amendmentFactRows = asArray(data.amendment_fact_comparison);
  const amendmentFactsChanged = amendmentFactRows.some(
    (fact) => fact.facts_changed === true,
  );
  const amendmentFacts = amendmentFactRows.flatMap((fact) => {
    const side = stringValue(fact.punch_type) === "out" ? "下班" : "上班";
    const deviation = fact.shift_deviation_minutes;
    const actual = formatBusinessDateTime(stringValue(fact.actual_at, ""), {
      seconds: false,
      fallback: "缺卡",
    });
    const requested = formatBusinessDateTime(
      stringValue(fact.requested_at, ""),
      {
        seconds: false,
        fallback: "—",
      },
    );
    const effective = formatBusinessDateTime(
      stringValue(fact.effective_at, ""),
      {
        seconds: false,
        fallback: "—",
      },
    );
    const shift = formatBusinessDateTime(stringValue(fact.shift_at, ""), {
      seconds: false,
      fallback: "未排班",
    });
    return [
      {
        label: `${side} · 实际 / 申请 / 生效`,
        value: `${actual} / ${requested} / ${effective}`,
      },
      {
        label: `${side} · 班次参考`,
        value: `${shift}${typeof deviation === "number" ? ` · 偏离 ${Math.abs(deviation)} 分钟` : ""}`,
      },
    ];
  });

  return {
    ...item,
    requestType,
    risk: parsedPayload.risk,
    summary: parsedPayload.summary,
    version: numberValue(data.version),
    payloadRows: parsedPayload.rows,
    reimbursementReceiptCount:
      requestType === "reimbursement"
        ? asArray(payload.receipt_photos).length
        : 0,
    leaveUnits: parsedLeave.units,
    leaveLegacy: parsedLeave.legacy,
    leaveQuota: parsedLeave.quota,
    scheduleChangeTargetName: parsedScheduleChange.targetName,
    scheduleChanges: parsedScheduleChange.changes,
    scheduleChangeReason: parsedScheduleChange.reason,
    scheduleChangeSupersededText:
      item.status === "expired" ? parsedScheduleChange.supersededText : "",
    submittedAtFull: formatDateTime(data.submitted_at),
    requesterPhone: stringValue(requester.phone),
    requesterHireDate: stringValue(requester.hire_date),
    existingPunches: punches,
    amendmentFacts,
    amendmentFactsChanged,
    riskSignals: {
      requesterThisMonth: numberValue(
        riskSignals.requester_amendment_count_this_month,
      ),
      requesterLast30d: numberValue(
        riskSignals.requester_amendment_count_last_30d,
      ),
      storeThisMonth: numberValue(riskSignals.store_amendment_count_this_month),
      storeLast30d: numberValue(riskSignals.store_amendment_count_last_30d),
    },
    approvalChain: parsedPayload.chain,
    steps,
    stepsError,
    statusSemantics: statusSemantics.display_label
      ? {
          status_code: stringValue(statusSemantics.status_code),
          display_label: stringValue(statusSemantics.display_label),
          current_owner: {
            role: stringValue(currentOwner.role) || null,
            role_label: stringValue(currentOwner.role_label) || null,
            employee_id:
              typeof currentOwner.employee_id === "number"
                ? currentOwner.employee_id
                : null,
            name: stringValue(currentOwner.name) || null,
          },
          available_actions: stringArray(statusSemantics.available_actions),
          next_step: stringValue(statusSemantics.next_step),
          deadline_at: stringValue(statusSemantics.deadline_at) || null,
          waiting_seconds: numberValue(statusSemantics.waiting_seconds),
          expected_seconds: numberValue(statusSemantics.expected_seconds),
        }
      : null,
    rejectReason: stringValue(data.reject_reason, ""),
    isSelfRequest: data.is_self_request === true,
    canDecide: data.can_decide === true,
  };
}
